/**
 * TanStack Query Hook for Deeptech Startups KPI
 *
 * This hook provides access to deeptech startups KPI data from the database,
 * including count of active deeptech startups and year-over-year growth percentage.
 *
 * Database Source: web.v_kpi_deeptech_startups_v1 view
 *
 * @module useKpiDeeptechStartupsV1
 * @category Database Hooks
 */

import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

/**
 * Interface for Deeptech Startups KPI data
 */
interface KpiDeeptechStartups {
  deeptech_count: number;
  deeptech_growth_percentage: number;
}

/**
 * Fetches deeptech startups KPI data from the database
 *
 * @returns {Promise<KpiDeeptechStartups>} KPI data object with count and growth percentage
 * @throws {Error} If the database query fails or returns invalid data
 * @internal
 */
const fetchKpiDeeptechStartups = async (): Promise<KpiDeeptechStartups> => {
  const { data, error } = await supabase
    .schema('web')
    .from('v_kpi_deeptech_startups_v1')
    .select('*')
    .single();

  if (error) {
    throw new Error(`Failed to fetch deeptech startups KPI: ${error.message}`);
  }

  if (!data) {
    throw new Error('No data returned from deeptech startups KPI query');
  }

  return data;
};

/**
 * React Hook for Deeptech Startups KPI Data
 *
 * Fetches and caches the deeptech startups KPI data using TanStack Query.
 * Includes count of active deeptech startups and YoY growth percentage.
 *
 * Query Configuration:
 * - Query Key: ['kpi', 'deeptech-startups', 'v1']
 * - Stale Time: 1 hour (3600000ms)
 * - Refetch on Window Focus: false
 * - Refetch on Mount: false
 * - Retry Attempts: 2
 *
 * @returns {UseQueryResult<KpiDeeptechStartups>} TanStack Query result object
 *
 * @example
 * ```typescript
 * const { data, isLoading, isError } = useKpiDeeptechStartupsV1();
 *
 * if (isLoading) return <Spinner />;
 * if (isError) return <ErrorMessage />;
 *
 * return (
 *   <div>
 *     <p>Deeptech Startups: {data.deeptech_count}</p>
 *     <p>Growth: +{data.deeptech_growth_percentage}%</p>
 *   </div>
 * );
 * ```
 */
export const useKpiDeeptechStartupsV1 = (): UseQueryResult<KpiDeeptechStartups> => {
  return useQuery<KpiDeeptechStartups>({
    queryKey: ['kpi', 'deeptech-startups', 'v1'],
    queryFn: fetchKpiDeeptechStartups,
    staleTime: 3600000, // 1 hour in milliseconds
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 2,
  });
};
